import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { ApplicationGateStatus } from "./application-gate-state.type";
import { TermsAndConditionGateService } from "./services/terms-and-condition-gate.service";
import { LogService } from "root/services/logservice/log.service";
import { MibpSessionService } from "root/services/mibp-session/mibp-session.service";
import { LoaderComponent } from "root/shared/components/status/loader/loader.component";
/**
 * This component serves as a gatekeeper for the application, ensuring that necessary conditions are met before allowing access to the main content. It can be used to check for user authentication, application initialization, or any other prerequisite before rendering the main application components. The template can be customized to display loading indicators, error messages, or redirect users as needed based on the application's requirements.
 * Main application content is blocked until the gate conditions are satisfied, providing a controlled entry point to the application and enhancing security and user experience.
 * 
 * Current gates:
 * - Terms and conditions
 */

@Component({
  selector: 'mibp-application-gate',
  templateUrl: './application-gate.component.html', 
  providers: [TermsAndConditionGateService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LoaderComponent]
})
export class MibpApplicationGateComponent implements OnInit {

  protected status = signal<ApplicationGateStatus>('checking');
  private termsGateService = inject(TermsAndConditionGateService);
  private session = inject(MibpSessionService);
  private log = inject(LogService).withPrefix(this.constructor.name);

  ngOnInit(): void {
    this.checkGates();
  }


  private async checkGates(): Promise<void> {
    this.log.debug('Checking application gates...');

    // Not logged in? Ok, you don't have to be logged in to see anonymous content, and you don't need to accept terms and conditions then, so we are ready.
    if (!this.session.isLoggedIn()) {
      this.status.set('ready');
      return;
    }

    await this.checkTermsAndConditions();
    this.log.debug('Finished checking application gates ');
  }


  private async checkTermsAndConditions(): Promise<void> {
    this.log.debug('Checking terms and conditions');
    try {
      // This promise will only resolve if user has accepted terms and conditions, 
      // or if this specific user does not need to accept them. Otherwise it will never resolve.
      await this.termsGateService.assertTermsAndConditions();
      this.status.set('ready');
    } catch (error) {
      this.log.error('Error while checking application gates', error);
      this.status.set('error');
    }
  }
  
  

}
